/**
 * PostHog error noise filter — capture-time before_send processing.
 *
 * Implements suppress / group / transform / rate-limit decisions for
 * known noisy error classes identified by the ENG-3879 audit.
 *
 * Per rule: code-comments-required (ai.knowledge/global)
 * Per rule: reference-rules-in-comments (ai.knowledge/global)
 */

import { COMMON_PROPERTIES, EVENT_SEVERITY } from '#~/analytics/constants/index.js';
import {
  EXCEPTION_FINGERPRINTS,
  GENERIC_EXCEPTION_TYPES,
  exceptionCatalogTags,
  resolveExceptionCatalogEntry
} from '#~/analytics/constants/exception-catalog.js';
import {
  CONNECT_FAILURE_PROPERTIES,
  classifyWebConnectFailure,
  connectFailureProductTags,
  isStandaloneWebClient
} from './web-connect-failures.js';
import {
  POSTHOG_EXCEPTION_LEVEL,
  POSTHOG_EXCEPTION_PROPERTY_NAMES,
  type PostHogExceptionLevel
} from './event-names.js';

// Types for the filter decisions
type FilterDecision = 'pass' | 'suppress' | 'transform' | 'rate_limited';

interface FilterOptions {
  verbose?: boolean;
}

interface ExceptionProperties {
  [key: string]: unknown;
  $exception_fingerprint?: string[] | string;
  $exception_list?: unknown;
  $exception_message?: string;
  $exception_type?: string;
}

interface FilterResult {
  decision: FilterDecision;
  fingerprint?: string;
  properties?: ExceptionProperties;
  ruleId?: string;
}

interface ExceptionListEntry {
  [key: string]: unknown;
  message?: unknown;
  type?: unknown;
  value?: unknown;
}

// Rate limiter: track event counts per key within a sliding window
// to prevent retry loops from flooding PostHog (e.g., Auth0 refresh token)
interface RateLimitEntry {
  count: number;
  windowStart: number;
}

const rateLimitMap = new Map<string, RateLimitEntry>();
const RATE_LIMIT_WINDOW_MS = 60_000; // 1 minute
const RATE_LIMIT_MAX = 5; // max events per key per window

function isRateLimited(key: string): boolean {
  const now = Date.now();
  const entry = rateLimitMap.get(key);

  if (!entry || now - entry.windowStart > RATE_LIMIT_WINDOW_MS) {
    rateLimitMap.set(key, { count: 1, windowStart: now });

    return false;
  }

  entry.count++;

  return entry.count > RATE_LIMIT_MAX;
}

// ── Suppression rules ──────────────────────────────────────────────
// LiveKit teardown / WebRTC lifecycle errors that are expected during
// normal room disconnect. Verbose mode keeps bounded visibility for audits.

interface SuppressionRule {
  fingerprint: string;
  message?: RegExp;
  ruleId: string;
  type?: RegExp;
}

const SUPPRESS_PATTERNS: SuppressionRule[] = [
  // §2b#1: UnexpectedConnectionState - PC manager is closed
  {
    ruleId: 'livekit-pc-manager-closed',
    fingerprint: EXCEPTION_FINGERPRINTS.LIVEKIT_PC_MANAGER_CLOSED,
    type: /unexpectedconnectionstate/i,
    message: /pc manager is closed/i
  },
  // §2b#2: LiveKit metadata update timeout
  {
    ruleId: 'livekit-metadata-update-timeout',
    fingerprint: EXCEPTION_FINGERPRINTS.LIVEKIT_METADATA_UPDATE_TIMEOUT,
    type: /livekiterror/i,
    message: /request to update local metadata timed out/i
  },
  // §2b#7: DOMException - addIceCandidate on closed pc
  {
    ruleId: 'webrtc-add-ice-candidate-closed',
    fingerprint: EXCEPTION_FINGERPRINTS.WEBRTC_ADD_ICE_CANDIDATE_CLOSED,
    type: /domexception/i,
    message: /addicecandidate.*closed/i
  },
  // §2b#8: DOMException - Unmounting before effect completed
  {
    ruleId: 'react-effect-unmounted-before-complete',
    fingerprint: EXCEPTION_FINGERPRINTS.REACT_EFFECT_UNMOUNTED_BEFORE_COMPLETE,
    type: /domexception/i,
    message: /unmounting before the effect has completed/i
  },
  // React StrictMode double-render noise
  {
    ruleId: 'browser-operation-aborted',
    fingerprint: EXCEPTION_FINGERPRINTS.BROWSER_OPERATION_ABORTED,
    type: /aborterror/i,
    message: /the operation was aborted/i
  },
  {
    ruleId: 'browser-aborterror-operation-aborted-message',
    fingerprint: EXCEPTION_FINGERPRINTS.BROWSER_OPERATION_ABORTED,
    message: /aborterror.*the operation was aborted/i
  },
  // Renderer-only: in the standalone web build there is no Electron IPC, so
  // the local-bridge guard throws by design. It is expected, not a defect.
  {
    ruleId: 'electron-only-local-connection',
    fingerprint: EXCEPTION_FINGERPRINTS.BRIDGE_ELECTRON_ONLY_LOCAL_CONNECTION,
    message: /local connection is only supported in the electron app/i
  }
];

// ── Grouping rules ─────────────────────────────────────────────────
// Errors that need stable fingerprints to collapse fragmented issues.

interface GroupingRule {
  fingerprint: string;
  match: (type?: string, message?: string) => boolean;
  transform?: (props: ExceptionProperties) => ExceptionProperties;
}

const isExceptionListEntry = (entry: unknown): entry is ExceptionListEntry =>
  !!entry && typeof entry === 'object';

const firstExceptionEntry = (properties: ExceptionProperties): ExceptionListEntry | undefined => {
  const exceptionList = Array.isArray(properties.$exception_list)
    ? <unknown[]>properties.$exception_list
    : [];

  const [entry] = exceptionList;

  return isExceptionListEntry(entry) ? entry : undefined;
};

const firstExceptionString = (
  properties: ExceptionProperties,
  key: keyof Pick<ExceptionListEntry, 'message' | 'type' | 'value'>
): string | undefined => {
  const value = firstExceptionEntry(properties)?.[key];

  return typeof value === 'string' ? value : undefined;
};

const normalizedExceptionFields = (
  properties: ExceptionProperties
): { message?: string; type?: string } => {
  const type =
    typeof properties.$exception_type === 'string'
      ? properties.$exception_type
      : firstExceptionString(properties, 'type');

  const message =
    typeof properties.$exception_message === 'string'
      ? properties.$exception_message
      : (firstExceptionString(properties, 'value') ?? firstExceptionString(properties, 'message'));

  return {
    ...(type ? { type } : {}),
    ...(message ? { message } : {})
  };
};

const withNormalizedExceptionFields = (
  properties: ExceptionProperties,
  type?: string,
  message?: string
): ExceptionProperties => ({
  ...properties,
  ...(type && typeof properties.$exception_type !== 'string' ? { $exception_type: type } : {}),
  ...(message && typeof properties.$exception_message !== 'string'
    ? { $exception_message: message }
    : {})
});

const withFirstExceptionValue = (
  properties: ExceptionProperties,
  value: string
): ExceptionProperties => {
  if (!Array.isArray(properties.$exception_list)) {
    return properties;
  }

  const exceptionList = <unknown[]>properties.$exception_list;
  const [first, ...rest] = exceptionList;

  if (!isExceptionListEntry(first)) {
    return properties;
  }

  return {
    ...properties,
    $exception_list: [{ ...first, value }, ...rest]
  };
};

const withIssueDisplayText = (
  properties: ExceptionProperties,
  title: string,
  description: string
): ExceptionProperties => {
  const withDisplayValue = withFirstExceptionValue(
    {
      ...properties,
      $exception_type: title,
      $exception_message: description
    },
    description
  );

  if (!Array.isArray(withDisplayValue.$exception_list)) {
    return withDisplayValue;
  }

  const exceptionList = <unknown[]>withDisplayValue.$exception_list;
  const [first, ...rest] = exceptionList;

  if (!isExceptionListEntry(first)) {
    return withDisplayValue;
  }

  // PostHog renders the bold issue title from the exception type path.
  return {
    ...withDisplayValue,
    $exception_list: [{ ...first, type: title, value: description }, ...rest]
  };
};

const stringProperty = (properties: ExceptionProperties, key: string): string | undefined => {
  const value = properties[key];
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined;
};

const toFingerprintPart = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'unknown';

const stableHash = (value: string): string => {
  let hash = 0x811c9dc5;
  for (let i = 0; i < value.length; i++) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(36);
};

const humanizeIdentifier = (value: string): string =>
  value
    .replace(/[_:-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const issueSubsystem = (properties: ExceptionProperties): string =>
  stringProperty(properties, 'posthog_issue_subsystem') ??
  stringProperty(properties, 'subsystem') ??
  stringProperty(properties, '$exception_component') ??
  stringProperty(properties, 'component') ??
  'renderer';

const fallbackIssueTitle = (type: string | undefined, subsystem: string): string => {
  const trimmedType = type?.trim();
  const source = humanizeIdentifier(subsystem);

  if (trimmedType && !GENERIC_EXCEPTION_TYPES.has(trimmedType)) {
    return trimmedType.includes(' ') ? trimmedType : humanizeIdentifier(trimmedType);
  }

  const typeLabel =
    trimmedType && trimmedType !== 'Error' ? humanizeIdentifier(trimmedType) : 'Error';

  return `Unhandled ${source} ${typeLabel}`;
};

const fallbackIssueDescription = (
  message: string | undefined,
  title: string,
  subsystem: string
): string => {
  const trimmedMessage = message?.trim();
  if (trimmedMessage && trimmedMessage !== title) return trimmedMessage;

  return `PostHog captured an unclassified ${humanizeIdentifier(subsystem).toLowerCase()} exception without a readable message.`;
};

const fallbackIssueFingerprint = (
  properties: ExceptionProperties,
  type: string | undefined,
  message: string | undefined,
  subsystem: string
): string | string[] => {
  const explicit = stringProperty(properties, 'posthog_issue_fingerprint');
  if (explicit) return explicit;
  if (properties.$exception_fingerprint) return properties.$exception_fingerprint;

  const stack = stringProperty(properties, '$exception_stack_trace_raw') ?? '';
  return `studio:${toFingerprintPart(subsystem)}:unclassified:${stableHash(
    [type ?? 'Error', message ?? '', stack].join('|')
  )}`;
};

const normalizeTrackedException = (
  properties: ExceptionProperties,
  type?: string,
  message?: string,
  reason: 'tracked_error_metadata' | 'fallback_unclassified' = 'fallback_unclassified'
): ExceptionProperties => {
  const subsystem = issueSubsystem(properties);
  const title =
    stringProperty(properties, 'posthog_issue_title') ?? fallbackIssueTitle(type, subsystem);
  const description =
    stringProperty(properties, 'posthog_issue_description') ??
    fallbackIssueDescription(message, title, subsystem);
  const fingerprint = fallbackIssueFingerprint(properties, type, message, subsystem);

  return {
    ...withIssueDisplayText(properties, title, description),
    $exception_fingerprint: fingerprint,
    posthog_error_contract: 'title-description-v1',
    posthog_normalization_reason: reason,
    posthog_original_exception_message: message,
    posthog_original_exception_type: type,
    subsystem,
    // A TrackedPostHogError may name a catalogued fingerprint explicitly; stamp
    // its product tags too so the same failure reads identically whether it
    // arrived through a grouping rule or through tracked metadata (ENG-4308).
    ...exceptionCatalogTags(fingerprint)
  };
};

const GROUPING_RULES: GroupingRule[] = [
  // Ambiguous LiveKit connection failures can be teardown noise or a real
  // outage. Group and rate-limit them instead of dropping all visibility.
  {
    match: (type, message) =>
      !!type &&
      !!message &&
      /connectionerror/i.test(type) &&
      /could not establish signal connection.*failed to fetch/i.test(message),
    fingerprint: EXCEPTION_FINGERPRINTS.LIVEKIT_SIGNAL_CONNECTION_FAILED
  },
  {
    match: (type, message) =>
      !!type &&
      !!message &&
      /connectionerror/i.test(type) &&
      /could not establish publisher connection.*closed/i.test(message),
    fingerprint: EXCEPTION_FINGERPRINTS.LIVEKIT_PUBLISHER_CONNECTION_CLOSED
  },
  {
    match: (_type, message) => !!message && /negotiation timed out/i.test(message),
    fingerprint: EXCEPTION_FINGERPRINTS.LIVEKIT_NEGOTIATION_TIMEOUT
  },
  // §2c: State-sync "synchronization already in progress" — 81K events
  // split across 10 distinct issue IDs due to bundle-hash chunk names
  {
    match: (_type, message) => !!message && /synchronization already in progress/i.test(message),
    fingerprint: EXCEPTION_FINGERPRINTS.STATE_SYNC_SYNCHRONIZATION_IN_PROGRESS
  },
  // §2b#6: Auth0 Missing Refresh Token — 14K events / 3 users / 30 days
  // Transform: lift audience into a property, canonicalize message, rate-limit
  {
    match: (_type, message) => !!message && /missing refresh token/i.test(message),
    fingerprint: EXCEPTION_FINGERPRINTS.AUTH_MISSING_REFRESH_TOKEN,
    transform: (props) => {
      // Extract audience from the message if present. The issue title/description
      // is applied centrally from the catalog, so only the rule-specific
      // enrichment lives here.
      const audienceMatch = props.$exception_message?.match(/\(audience:\s*([^)]+)\)/);

      return {
        ...props,
        auth_audience: audienceMatch?.[1] ?? 'unknown'
      };
    }
  }
];

/**
 * Apply the catalog's human what/why as the PostHog Issue display text and stamp
 * the queryable product tags. Centralised so every catalogued fingerprint gets
 * the identical treatment and the strings live in exactly one place
 * (ENG-4308 / MSG). Uncatalogued fingerprints pass through untouched.
 */
const withExceptionCatalog = (
  properties: ExceptionProperties,
  fingerprint: string
): ExceptionProperties => {
  const entry = resolveExceptionCatalogEntry(fingerprint);

  if (!entry) {
    return properties;
  }

  return {
    ...withIssueDisplayText(properties, entry.what, entry.why),
    ...exceptionCatalogTags(fingerprint)
  };
};

// Derive the product-facing `subsystem` tag from a `studio:<subsystem>:<issue>`
// fingerprint so the dashboard gains a single queryable dimension
// (livekit, webrtc, certs, bridge, …) without tagging each rule by hand.
// Mirrors posthog-noise-filter.ts in the main process.
// Per rule: think-in-systems (ai.knowledge/global) — single derivation point.
const subsystemFromFingerprint = (fingerprint: string): string | undefined => {
  const parts = fingerprint.split(':');

  return parts.length >= 3 && parts[0] === 'studio' ? parts[1] : undefined;
};

/**
 * Per ENG-4326 (X-1b): grade the crash class from the REVIEWED severity of the
 * named failure, not from `mechanism.handled`.
 *
 * WHY NOT `mechanism.handled`. posthog-js tags it truthfully, but it records how
 * PostHog learned about the error, not how bad the error is. Renderer autocapture
 * only ever sees `window.onerror` / `onunhandledrejection`, which are unhandled by
 * construction, so the bit is a constant here: production project 70344 measured
 * 7423 of 7423 renderer `$exception` events at `handled: false` over 7 days. A
 * level that fires on every row cannot narrow anything, which is the one job
 * `fatal` has in PostHog Issues. The same rule earns its place in the Electron
 * main process (12 fatal against 10 handled-true in the same window) because main
 * DOES report caught failures deliberately — same rule, different population.
 * The bit is also already queryable on its own: `$exception_handled` is present on
 * 38230 of 38230 events, so copying it into the level adds no information.
 *
 * WHAT THIS USES INSTEAD. `exception-catalog.ts` is the signed-off ENG-4300 audit:
 * one entry per NAMED failure, each carrying a reviewed `severity`. `S1/fatal`
 * means the app cannot continue. Projecting that onto `$exception_level` is the
 * pattern `resolveEncoderExceptionLevel` (encoder-errors.ts) already ships, so the
 * renderer gains no third rule. Anything the catalog does not name stays `error`
 * until a human adds it, which is why `fatal` is expected to be rare — and rare is
 * the property that makes it a usable filter.
 *
 * The catalog is the ONLY input (ENG-4607 scope: `fatal` if and only if the
 * outgoing severity is `S1/fatal`). Any incoming `$exception_level` is
 * overwritten, including a caller's own `fatal`: a failure that should count as a
 * crash gets a catalog row, which is where the review happens. No renderer
 * producer sends a pre-graded `$exception` today (posthog-js always stamps
 * `error`; encoder and onboarding failures use other event names and never reach
 * this filter), so honouring one would only open an unreviewed route into the
 * crash cohort.
 *
 * Read from the OUTGOING properties: the catalog `severity` tag is stamped by
 * `withExceptionCatalog` / `normalizeTrackedException` during classification, and
 * the web connect-failure tags by `withWebConnectFailureTags`. Grading after both
 * keeps one derivation point (the catalog) instead of re-deriving severity here.
 *
 * `$exception_level` is per event and is never back-filled, so this grades events
 * captured from here forward only.
 *
 * Per rule: think-in-systems (ai.knowledge/global) — the catalog is the one source.
 * Per rule: reference-rules-in-comments (ai.knowledge/global)
 */
const resolveExceptionLevel = (outgoing: ExceptionProperties): PostHogExceptionLevel => {
  return outgoing[COMMON_PROPERTIES.SEVERITY] === EVENT_SEVERITY.S1_FATAL
    ? POSTHOG_EXCEPTION_LEVEL.FATAL
    : POSTHOG_EXCEPTION_LEVEL.ERROR;
};

/**
 * Stamp LEVEL on every outgoing exception. `suppress` and `rate_limited` carry no
 * properties — there is no outgoing event to grade.
 *
 * Runs OUTERMOST in `filterException` so the catalog and web-connect severity tags
 * are already on the bag it reads.
 */
const withExceptionLevel = (result: FilterResult): FilterResult => {
  if (!result.properties) return result;

  return {
    ...result,
    properties: {
      ...result.properties,
      [POSTHOG_EXCEPTION_PROPERTY_NAMES.LEVEL]: resolveExceptionLevel(result.properties)
    }
  };
};

/**
 * Stamp the product tags (severity / blocking / what / why) on any named group
 * leaving the filter for a standalone-web client.
 *
 * WHY THIS IS AT THE EXIT RATHER THAN INSIDE THE BRANCH THAT NAMES THE GROUP.
 * `filterException` has four return paths that assign a fingerprint. The Phase 1a
 * web classification originally stamped its properties inside its own return, which
 * made the tags a property of that one branch. A sibling change introducing
 * exception-catalog.ts independently wraps the other three in a catalog helper.
 * Merge-simulating the two showed the predictable result: one conflict, in the
 * import block, resolved mechanically —
 * and web connect failures come out the far side with no `severity` and no
 * `blocking`, so every failure surface that filters on severity (the mandated
 * approach, so `discovery_empty`'s S3/warn can be excluded by property instead of
 * by a name list) silently returns zero web connect failures. Both PRs' suites
 * stay green because neither knows about the other's return path.
 *
 * Applying the tags where the result LEAVES the function removes that class of
 * bypass instead of patching this instance of it: a naming return added anywhere
 * inside `classifyException`, by anyone, later, passes through here.
 *
 * Gated on the browser client so the desktop build is byte-for-byte unchanged.
 * Three of these fingerprints are pre-existing desktop groups, and tagging them
 * on Electron would both alter desktop behaviour and pre-empt the exception
 * catalog, which owns the desktop groups' tags.
 *
 * Per rule: think-in-systems (ai.knowledge/global) — one application point.
 * Per rule: reference-rules-in-comments (ai.knowledge/global).
 */
const withWebConnectFailureTags = (
  result: FilterResult,
  incoming: ExceptionProperties
): FilterResult => {
  if (!isStandaloneWebClient(incoming[COMMON_PROPERTIES.CLIENT])) return result;

  const tags = connectFailureProductTags(result.fingerprint);

  // `suppress` and `rate_limited` carry no properties — there is no outgoing
  // event to tag, and inventing one would change the decision.
  if (!tags || !result.properties) return result;

  return { ...result, properties: { ...result.properties, ...tags } };
};

/**
 * Filter an exception event before it reaches PostHog.
 * Called from PostHogManager during event processing.
 *
 * Deliberately a thin delegation: every result passes through
 * `withWebConnectFailureTags` and then `withExceptionLevel` on its way out, which
 * is the invariant that stops a named group from shipping untagged or ungraded.
 * The grade runs last because it READS the severity those tags supply.
 * See those functions for why.
 *
 * @param properties - The exception event properties.
 * @returns FilterResult with decision and optionally transformed properties.
 */
export function filterException(
  properties: ExceptionProperties,
  options: FilterOptions = {}
): FilterResult {
  return withExceptionLevel(
    withWebConnectFailureTags(classifyException(properties, options), properties)
  );
}

/**
 * The classification itself: suppress / group / transform / rate-limit. Not
 * exported — callers go through `filterException` so the exit stamp cannot be
 * skipped by reaching in here directly.
 */
function classifyException(
  properties: ExceptionProperties,
  options: FilterOptions = {}
): FilterResult {
  const { message, type } = normalizedExceptionFields(properties);
  const normalizedProperties = withNormalizedExceptionFields(properties, type, message);

  const hasTrackedMetadata =
    !!stringProperty(normalizedProperties, 'posthog_issue_title') ||
    !!stringProperty(normalizedProperties, 'posthog_issue_description') ||
    !!stringProperty(normalizedProperties, 'posthog_issue_fingerprint');

  if (hasTrackedMetadata) {
    const transformed = normalizeTrackedException(
      normalizedProperties,
      type,
      message,
      'tracked_error_metadata'
    );

    const fingerprint =
      typeof transformed.$exception_fingerprint === 'string'
        ? transformed.$exception_fingerprint
        : undefined;

    return {
      decision: 'transform',
      ...(fingerprint ? { fingerprint } : {}),
      properties: transformed
    };
  }

  // Phase 1a: standalone-web LiveKit/WebRTC CONNECT failures get a
  // named group before the suppression sweep can drop them. This runs first
  // because several of these shapes are indistinguishable from desktop teardown
  // noise by type+message alone, and on desktop that reading is correct — the
  // classifier returns null for any client other than standalone-web, so the
  // Electron path below is reached with the same properties it always was.
  // Rate-limited on the shared limiter so a browser reconnect loop cannot flood
  // a single group.
  const webConnectFailure = classifyWebConnectFailure({
    client: properties[COMMON_PROPERTIES.CLIENT],
    type,
    message
  });

  if (webConnectFailure) {
    if (isRateLimited(webConnectFailure.fingerprint)) {
      return { decision: 'rate_limited', fingerprint: webConnectFailure.fingerprint };
    }

    // Same stamping shape as the desktop GROUPING_RULES path below (fingerprint
    // + subsystem + title/description display text), so one consumer reads web
    // and desktop groups identically and `client` is the only discriminator.
    // The severity / blocking / what / why tags are NOT stamped here on purpose —
    // `withWebConnectFailureTags` applies them once at the function's exit so
    // this branch is not the thing that owns them. See that function.
    const classified = withIssueDisplayText(
      {
        ...normalizedProperties,
        $exception_fingerprint: webConnectFailure.fingerprint,
        [CONNECT_FAILURE_PROPERTIES.SUBSYSTEM]: webConnectFailure.subsystem,
        [CONNECT_FAILURE_PROPERTIES.KIND]: webConnectFailure.issue
      },
      webConnectFailure.title,
      webConnectFailure.description
    );

    return {
      decision: 'transform',
      fingerprint: webConnectFailure.fingerprint,
      ruleId: webConnectFailure.issue,
      properties: classified
    };
  }

  // Phase 1: Check suppression rules
  for (const pattern of SUPPRESS_PATTERNS) {
    const typeMatch = !pattern.type || (type && pattern.type.test(type));
    const messageMatch = !pattern.message || (message && pattern.message.test(message));

    if (typeMatch && messageMatch) {
      if (!options.verbose) {
        return {
          decision: 'suppress',
          fingerprint: pattern.fingerprint,
          ruleId: pattern.ruleId
        };
      }

      if (isRateLimited(pattern.fingerprint)) {
        return {
          decision: 'rate_limited',
          fingerprint: pattern.fingerprint,
          ruleId: pattern.ruleId
        };
      }

      const suppressSubsystem = subsystemFromFingerprint(pattern.fingerprint);

      return {
        decision: 'transform',
        fingerprint: pattern.fingerprint,
        ruleId: pattern.ruleId,
        properties: withExceptionCatalog(
          {
            ...normalizedProperties,
            $exception_fingerprint: pattern.fingerprint,
            filter_decision: 'verbose_sent',
            noise: true,
            noise_rule: pattern.ruleId,
            ...(suppressSubsystem ? { subsystem: suppressSubsystem } : {})
          },
          pattern.fingerprint
        )
      };
    }
  }

  // Phase 2: Check grouping rules
  for (const rule of GROUPING_RULES) {
    if (rule.match(type, message)) {
      // Apply rate limiting on grouped errors
      if (isRateLimited(rule.fingerprint)) {
        return { decision: 'rate_limited', fingerprint: rule.fingerprint };
      }

      const subsystem = subsystemFromFingerprint(rule.fingerprint);
      const withFingerprint: ExceptionProperties = {
        ...normalizedProperties,
        $exception_fingerprint: rule.fingerprint,
        // Tag survives rule.transform — transforms spread `...props`.
        ...(subsystem ? { subsystem } : {})
      };

      const transformed = rule.transform ? rule.transform(withFingerprint) : withFingerprint;

      return {
        decision: 'transform',
        fingerprint: rule.fingerprint,
        properties: withExceptionCatalog(transformed, rule.fingerprint)
      };
    }
  }

  // Phase 3: normalize any remaining tracked exception before PostHog sees it.
  return {
    decision: 'transform',
    properties: normalizeTrackedException(normalizedProperties, type, message)
  };
}

// Export the rate limit map reset for testing
export function _resetRateLimits(): void {
  rateLimitMap.clear();
}

export { GROUPING_RULES, isRateLimited, RATE_LIMIT_MAX, RATE_LIMIT_WINDOW_MS, SUPPRESS_PATTERNS };
export type { ExceptionProperties, FilterDecision, FilterOptions, FilterResult };
